import React from 'react';
import { createPortal } from 'react-dom';
import { useEventsList } from '@/domain/events/useEvents';
import { useToasts } from '../../ui/ToastProvider';

type EventLinkSelectorProps = {
  open: boolean;
  /** The events linked now: they start ticked. Read when the selector opens, not on every render. */
  currentEventIds: string[];
  /** Saves the ticked set; the caller says what changed. A rejection keeps the selector open. */
  onSelect: (eventIds: string[]) => Promise<void>;
  onClose: () => void;
};

export default function EventLinkSelector({
  open,
  currentEventIds,
  onSelect,
  onClose,
}: EventLinkSelectorProps) {
  const { push } = useToasts();
  const titleId = React.useId();
  const [search, setSearch] = React.useState('');
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => new Set(currentEventIds));
  const [submitting, setSubmitting] = React.useState(false);
  const currentRef = React.useRef(currentEventIds);
  currentRef.current = currentEventIds;
  const wasOpenRef = React.useRef(open);

  const eventsQuery = useEventsList({
    filters: { search, sortBy: 'latest' },
  });
  const events = eventsQuery.data?.data ?? [];

  // Ticks and search start over only when the selector opens. The page behind re-renders while it
  // is open (its header melts on scroll, a save starts or ends), and that must not undo the ticks.
  React.useEffect(() => {
    if (open && !wasOpenRef.current) {
      setSelectedIds(new Set(currentRef.current));
      setSearch('');
    }
    wasOpenRef.current = open;
  }, [open]);

  const toggleEvent = React.useCallback((eventId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  }, []);

  const handleSubmit = React.useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSelect(Array.from(selectedIds));
      onClose();
    } catch (err) {
      push({ type: 'error', message: (err instanceof Error && err.message) || 'Failed to link events' });
    } finally {
      setSubmitting(false);
    }
  }, [selectedIds, onSelect, push, onClose]);

  if (!open) return null;

  const host = document.getElementById('portal-root') || document.body;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        zIndex: 14000,
      }}
    >
      <form
        onClick={e => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="stack"
        style={{
          width: 'min(600px, 95vw)',
          maxHeight: '80vh',
          overflowY: 'auto',
          background: 'var(--surface)',
          borderRadius: 12,
          boxShadow: 'var(--shadow-lg)',
          padding: '1.5rem',
          gap: '1rem',
        }}
      >
        <header className="stack gap-xs">
          <h2 id={titleId} className="m0 text-lg">Link Events</h2>
          <p className="m0 text-sm fg-muted">
            Select events to link to this transfer order.
          </p>
        </header>

        <section className="stack gap-sm">
          <input
            type="text"
            placeholder="Search events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="control"
            disabled={submitting}
          />

          <div className="stack gap-xxs" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {events.length === 0 && (
              <div className="text-sm fg-muted">No events found</div>
            )}
            {events.map(event => {
              const isSelected = selectedIds.has(event.id);
              return (
                <label
                  key={event.id}
                  className="flex gap-sm"
                  style={{ padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleEvent(event.id)}
                    disabled={submitting}
                  />
                  <div className="stack gap-xxs" style={{ flex: 1 }}>
                    <div className="text-sm fw-medium">
                      {event.event_code || event.id.slice(0, 8)} - {event.name || 'Unnamed'}
                    </div>
                    {event.show_start_date && (
                      <div className="text-xs fg-muted">
                        {new Date(event.show_start_date).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </section>

        <footer className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn sm subtle" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="btn sm" disabled={submitting}>
            {submitting ? 'Linking...' : 'Link Events'}
          </button>
        </footer>
      </form>
    </div>,
    host,
  );
}
